import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  ContractWithProviderDoc,
  ContractWithProviderReportRow,
  ContractWithProviderReconciliationRow,
  ContractLedgerResponse
} from 'src/typings';
import { environment } from 'src/environments/environment';
import {
  normalizeDateRangePayload,
  toLocalDateString
} from 'src/app/shared/utils/date-filter.util';

@Injectable({
  providedIn: 'root'
})
export class ContractWithProviderService {
  constructor(private http: HttpClient) {}

  getList() {
    return this.http
      .get<ContractWithProviderDoc[]>(`${environment.BACKEND}/doc/contract-with-provider/list`)
      .pipe(
        map((data) =>
          data.map((doc) => ({
            ...doc,
            start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
            end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
          }))
        )
      );
  }

  search(payload: {
    number?: string;
    provider_id?: number;
    org_id?: number;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.http
      .post<
        | ContractWithProviderDoc[]
        | { data: ContractWithProviderDoc[]; length?: number; total?: number; count?: number }
      >(`${environment.BACKEND}/doc/contract-with-provider/search`, payload)
      .pipe(
        map((response) => {
          const data = Array.isArray(response) ? response : response.data;
          const length = Array.isArray(response)
            ? response.length
            : response.length ?? response.total ?? response.count ?? response.data.length;
          return {
            data: data.map((doc) => ({
              ...doc,
              start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
              end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
            })),
            length
          };
        })
      );
  }

  report(payload: {
    number?: string;
    provider_id?: number;
    org_id?: number;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.http
      .post<{ data: ContractWithProviderReportRow[]; length: number }>(
        `${environment.BACKEND}/doc/contract-with-provider/report`,
        payload
      )
      .pipe(
        map((response) => ({
          data: response.data.map((doc) => ({
            ...doc,
            start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
            end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
          })),
          length: response.length
        }))
      );
  }

  reconciliation(payload: {
    number?: string;
    provider_id?: number;
    org_id?: number;
    active?: boolean;
    month?: number;
    year?: number;
    limit?: number;
    offset?: number;
  }) {
    return this.http.post<{ data: ContractWithProviderReconciliationRow[]; length: number }>(
      `${environment.BACKEND}/doc/contract-with-provider/reconciliation`,
      payload
    );
  }

  ledger(payload: { contract_id: number; date_from: Date; date_to: Date }) {
    const normalizedPayload = normalizeDateRangePayload(payload);
    return this.http.post<ContractLedgerResponse>(
      `${environment.BACKEND}/doc/contract-with-provider/ledger`,
      normalizedPayload
    );
  }

  ledgerPdf(payload: { contract_id: number; date_from: Date; date_to: Date }) {
    const dateFrom = toLocalDateString(payload.date_from);
    const dateTo = toLocalDateString(payload.date_to);
    let params = new HttpParams().set('contract_id', String(payload.contract_id));
    if (dateFrom) params = params.set('date_from', dateFrom);
    if (dateTo) params = params.set('date_to', dateTo);
    return this.http.get(`${environment.BACKEND}/doc/contract-with-provider/ledger/pdf`, {
      params,
      responseType: 'blob'
    });
  }

  getOne(id: number) {
    return this.http
      .get<ContractWithProviderDoc>(`${environment.BACKEND}/doc/contract-with-provider/${id}`)
      .pipe(
        map((doc) => ({
          ...doc,
          start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
          end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
        }))
      );
  }

  create(payload: {
    provider_id: number;
    org_id: number;
    number: string;
    start_date: Date | string | null;
    end_date: Date | string | null;
    day_limit: number;
    credit_limit: number;
    active: boolean;
  }) {
    return this.http.post<ContractWithProviderDoc>(
      `${environment.BACKEND}/doc/contract-with-provider/create`,
      {
        ...payload,
        start_date: toLocalDateString(payload.start_date) || null,
        end_date: toLocalDateString(payload.end_date) || null
      }
    ).pipe(map(doc => ({
      ...doc,
      start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
      end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
    })));
  }

  save(payload: {
    id: number;
    provider_id: number;
    org_id: number;
    number: string;
    start_date: Date;
    end_date: Date;
    day_limit: number;
    credit_limit: number;
    active: boolean;
  }) {
    return this.http.put<ContractWithProviderDoc>(
      `${environment.BACKEND}/doc/contract-with-provider/save`,
      { ...payload, start_date: toLocalDateString(payload.start_date) || null, end_date: toLocalDateString(payload.end_date) || null }
    ).pipe(map(doc => ({
      ...doc,
      start_date: doc.start_date ? new Date(doc.start_date) : doc.start_date,
      end_date: doc.end_date ? new Date(doc.end_date) : doc.end_date
    })));
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/doc/contract-with-provider/${id}`);
  }
}
